import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";

export const AddressService = {
  async getUserAddresses(userId) {
    try {
      const addressesQuery = query(
        collection(db, "userAddresses"),
        where("userId", "==", userId)
      );
      
      const snapshot = await getDocs(addressesQuery);
      const addresses = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort addresses: primary first, then by creation date (newest first)
      addresses.sort((a, b) => {
        // Primary addresses first
        if (a.isPrimary && !b.isPrimary) return -1;
        if (!a.isPrimary && b.isPrimary) return 1;
        
        // Then sort by creation date (newest first)
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      return {
        success: true,
        data: addresses,
      };
    } catch (error) {
      console.error("Error fetching user addresses:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  async addAddress(addressData) {
    try {
      // If this is being set as primary, first remove primary status from other addresses
      if (addressData.isPrimary) {
        await this.clearPrimaryAddress(addressData.userId);
      }

      const addressDoc = {
        ...addressData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, "userAddresses"), addressDoc);
      
      return {
        success: true,
        id: docRef.id,
        message: "Address added successfully",
      };
    } catch (error) {
      console.error("Error adding address:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  async updateAddress(addressId, addressData) {
    try {
      // If this is being set as primary, first remove primary status from other addresses
      if (addressData.isPrimary) {
        await this.clearPrimaryAddress(addressData.userId);
      }

      const addressRef = doc(db, "userAddresses", addressId);
      await updateDoc(addressRef, {
        ...addressData,
        updatedAt: serverTimestamp(),
      });

      return {
        success: true,
        message: "Address updated successfully",
      };
    } catch (error) {
      console.error("Error updating address:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  async deleteAddress(addressId) {
    try {
      await deleteDoc(doc(db, "userAddresses", addressId));
      
      return {
        success: true,
        message: "Address deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting address:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  async setPrimaryAddress(userId, addressId) {
    try {
      // First, clear primary status from all addresses
      await this.clearPrimaryAddress(userId);

      // Then set the new primary address
      const addressRef = doc(db, "userAddresses", addressId);
      await updateDoc(addressRef, {
        isPrimary: true,
        updatedAt: serverTimestamp(),
      });

      return {
        success: true,
        message: "Primary address updated successfully",
      };
    } catch (error) {
      console.error("Error setting primary address:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  async clearPrimaryAddress(userId) {
    try {
      const addressesQuery = query(
        collection(db, "userAddresses"),
        where("userId", "==", userId),
        where("isPrimary", "==", true)
      );
      
      const snapshot = await getDocs(addressesQuery);
      
      const updatePromises = snapshot.docs.map((doc) =>
        updateDoc(doc.ref, {
          isPrimary: false,
          updatedAt: serverTimestamp(),
        })
      );

      await Promise.all(updatePromises);

      return {
        success: true,
      };
    } catch (error) {
      console.error("Error clearing primary addresses:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  async getPrimaryAddress(userId) {
    try {
      const addressesQuery = query(
        collection(db, "userAddresses"),
        where("userId", "==", userId),
        where("isPrimary", "==", true)
      );
      
      const snapshot = await getDocs(addressesQuery);
      
      if (snapshot.empty) {
        return {
          success: true,
          data: null,
        };
      }

      const primaryAddress = {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data(),
      };

      return {
        success: true,
        data: primaryAddress,
      };
    } catch (error) {
      console.error("Error fetching primary address:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  validateAddress(address) {
    const errors = [];

    if (!address.fullName?.trim()) {
      errors.push("Full name is required");
    }

    if (!address.addressLine1?.trim()) {
      errors.push("Address line 1 is required");
    }

    if (!address.city?.trim()) {
      errors.push("City is required");
    }

    if (!address.state?.trim()) {
      errors.push("State is required");
    }

    if (!address.postalCode?.trim()) {
      errors.push("Postal code is required");
    } else if (!/^\d{6}$/.test(address.postalCode.trim())) {
      errors.push("Postal code must be 6 digits");
    }

    if (!address.phoneNumber?.trim()) {
      errors.push("Phone number is required");
    } else if (!/^\+?[\d\s-()]{10,}$/.test(address.phoneNumber.trim())) {
      errors.push("Please enter a valid phone number");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  formatAddressForDisplay(address) {
    const parts = [
      address.fullName,
      address.addressLine1,
      address.addressLine2,
      `${address.city}, ${address.state} ${address.postalCode}`,
      address.phoneNumber
    ].filter(Boolean);

    return parts.join(', ');
  },

  async getUserUpiId(userId) {
    try {
      const upiQuery = query(
        collection(db, "userUpiIds"),
        where("userId", "==", userId)
      );
      
      const snapshot = await getDocs(upiQuery);
      
      if (snapshot.empty) {
        return {
          success: true,
          data: null,
        };
      }

      const upiData = {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data(),
      };

      return {
        success: true,
        data: upiData,
      };
    } catch (error) {
      console.error("Error fetching UPI ID:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  async saveUserUpiId(userId, upiId) {
    try {
      const existingUpi = await this.getUserUpiId(userId);
      
      if (existingUpi.success && existingUpi.data) {
        return {
          success: false,
          error: "UPI ID already exists. Contact admin to change.",
        };
      }

      const upiDoc = {
        userId,
        upiId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, "userUpiIds"), upiDoc);
      
      // Also update the user document
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);
      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
        await updateDoc(userDocRef, {
          upiId: upiId,
          updatedAt: serverTimestamp(),
        });
      }
      
      return {
        success: true,
        message: "UPI ID saved successfully",
      };
    } catch (error) {
      console.error("Error saving UPI ID:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },
};